/*
  This component renders a list of child components that can be of varying heights. Items flow with
  normal block layout inside a scrollable container. To avoid rendering very long lists all at once,
  it renders items in batches and appends the next batch as the user scrolls near the bottom.
*/

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const BATCH_SIZE = 50; // how many items to reveal per append
const APPEND_THRESHOLD_PX = 1000; // append more once the bottom is within this distance

const Wrapper = styled.div`
  height: 100vh;
  min-height: 10em;
  overflow-y: auto;
`;

const SectionTitle = styled.div`
  background: var(--color-primary-05);
  font-weight: 500;
  ${({ theme }) => theme.fontSize('14px')};
  line-height: 22px;
  color: var(--color-primaryText);
  text-transform: uppercase;
  padding: 7px 16px;
  box-shadow:
    0px 1px 0px #ebebeb,
    0px -1px 0px #ebebeb;
`;

const Row = styled.div`
  border-top: 1px solid #ebebeb;
  &:first-of-type {
    border-top: none;
  }
`;

export const sectionTitleRow = sectionTitle => <SectionTitle>{sectionTitle}</SectionTitle>;

export const ResultList = ({ items, className }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  // Restart from the first batch whenever the source list changes (e.g. a new search).
  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [items]);

  const appendIfNeeded = () => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < APPEND_THRESHOLD_PX) {
      setVisibleCount(current => Math.min(current + BATCH_SIZE, items.length));
    }
  };

  // Keep topping up until the viewport is filled (or all items are shown), so a tall
  // viewport that isn't scrollable yet still reaches the full list.
  useEffect(() => {
    appendIfNeeded();
  }, [visibleCount, items]);

  return (
    <Wrapper ref={wrapperRef} className={className} onScroll={appendIfNeeded}>
      {items.slice(0, visibleCount).map((item, index) => (
        <Row key={index} data-testid={`result-row-${index}`}>
          {item}
        </Row>
      ))}
    </Wrapper>
  );
};
